/** A record page for every kind that is not an activity record — **EXP-15's remaining half**.
 *
 * `/records/{id}` rendered an activity record and said so politely for everything else: *"this
 * record exists and you can see it — just not on this page."* That was true and it was a dead end.
 * A risk, a source, a site, a document and an initiative all register in ADR-0037's supertype, all
 * carry tasks, notes, flags and approvals, and `/records/{id}/work` has answered for every one of
 * them over HTTP since S05. Only the page was missing.
 *
 * ## Two tabs, and the facts are borrowed rather than re-rendered
 *
 * **Details** shows the few facts that identify the record, read from whoever already owns them —
 * the risk register's own query, the plan, the census, the evidence library. Nothing here fetches a
 * kind's facts a second way, and nothing re-renders arithmetic: a figure's chain is the receipt
 * drawer's single rendering (rule 3), and a risk's score is deliberately **words**, because
 * `basis.Score` refuses to be multiplied and a page that printed `4 × 5` would undo that refusal in
 * CSS.
 *
 * **Workflow** is the same component the activity record uses, which is the point of the refactor:
 * one tab, one behaviour, nine kinds. See `RecordWork.tsx`.
 *
 * ## What it does not pretend to
 *
 * Three kinds have no cheap single read — a meter, a filing obligation and a disclosure statement —
 * so the Details tab **says where the detail lives** rather than fetching the whole organisation to
 * fill a header. An absence said beats a blank panel (rule 5), and it beats a loading spinner that
 * resolves to nothing.
 *
 * There is also no *correct this* tab for any kind. Each one means something different — a risk is
 * rescored against a scale, an initiative's achievement is a separate verb, a source is retired
 * rather than edited — and every one of those acts already has its own control on its own register.
 * A second one here would be a second path to the same write, which is how two screens come to
 * disagree.
 */

import { Files } from "@phosphor-icons/react";
import { Link, useParams } from "@tanstack/react-router";

import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { InfoPopover, RecordPage } from "../components/ui";
import { useDocument, useFacilities } from "../api/evidence";
import { usePlan, useReport, useRisks } from "../api/queries";
import { useCoverage } from "../api/sources";
import { useExchange } from "../api/suppliers";
import { quantity, shortDate } from "../lib/format";
import { recordPlace } from "../shell/routeTable";
import { ScreenFrame } from "./ScreenFrame";
import { KIND_HOME, kindLabel } from "./kinds";
import { VERDICT_HEADLINE } from "./verdictWords";
import { RecordDocumentView } from "./RecordDocument";
import { WorkflowTab } from "./RecordWork";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import type { Screen } from "./useScreen";
import styles from "./Screen.module.css";
import own from "./Record.module.css";

const TABS = [
  { key: "details", label: "Details" },
  { key: "approvals", label: "Approvals" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

function Pair({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <>
      <dt className={cls(own.k)}>{k}</dt>
      <dd className={cls(own.v, mono && own.mono)}>{v}</dd>
    </>
  );
}

/** What this record is called, for the page header.
 *
 * **A name, never an id**, and never the kind standing in for one: a page headed "Climate risk"
 * above four panels about *a* risk is a page a reader cannot tell from another tab of the same
 * kind. Where the name has not loaded yet the kind is the honest placeholder, and it is replaced
 * the moment the owning query answers.
 */
export function RecordOther({ screen, kind }: { screen: Screen; kind: string }) {
  const { recordId } = useParams({ from: "/records/$recordId" });
  // The old key for the second tab was `workflow`; a saved link to it opens Approvals.
  const tab: TabKey = screen.tab === "approvals" || screen.tab === "workflow" ? "approvals" : "details";

  const detail = useKindDetail(kind, recordId, screen);

  return (
    <ScreenFrame
      screen={screen}
      title={detail.title ?? kindLabel(kind)}
      crumb={kindLabel(kind)}
      lights={recordPlace(kind)}
    >
      <RecordPage
        icon={Files}
        title={detail.title ?? kindLabel(kind)}
        label={kindLabel(kind)}
        actions={<HomeLink kind={kind} />}
        tabs={TABS}
        tab={tab}
        onTab={(key) => screen.setTab(key)}
        saying={`Showing ${TABS.find((entry) => entry.key === tab)?.label ?? tab} for ${
          detail.title ? `${kindLabel(kind).toLowerCase()} ${detail.title}` : kindLabel(kind)
        }.`}
      >
        {tab === "details" ? (
          <>
          <Panel
            title="Details"
            column="1 / 9"
            action={kind === "document" ? <InfoPopover infoKey="record.document-review" /> : undefined}
          >
            {detail.pairs.length > 0 ? (
              <dl className={cls(own.kv)} data-testid="kind-details">
                {detail.pairs.map(([k, v]) => (
                  <Pair key={k} k={k} v={v} mono={k === "SHA-256"} />
                ))}
              </dl>
            ) : detail.failed ? (
              <ReadFailed
                of={`This ${kindLabel(kind).toLowerCase()}'s details`}
                error={detail.failed}
                onRetry={detail.retry ?? (() => undefined)}
              />
            ) : (
              <Empty>{detail.absent ?? "Loading…"}</Empty>
            )}
            {kind === "filing_obligation" ? (
              <p className={styles.note}>
                <Link
                  className={styles.fixLink}
                  to="/reports"
                  search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), filing: recordId })}
                  data-testid="open-filing"
                >
                  Open this filing in Reports ›
                </Link>
              </p>
            ) : null}
            {kind === "document" ? null : (
              <p className={styles.note} data-testid="kind-because">
                {detail.because ??
                  "These facts belong to the register that owns them, and this page reads them from " +
                    "there rather than keeping a second copy — two copies of a name eventually " +
                    "disagree, and the second one is the one nobody updates."}
              </p>
            )}
          </Panel>
          {kind === "document" && detail.documentState ? (
            <RecordDocumentView documentId={recordId} state={detail.documentState} />
          ) : null}
          </>
        ) : (
          <WorkflowTab recordId={recordId} kind={kind} revision={detail.revision} />
        )}
      </RecordPage>
    </ScreenFrame>
  );
}

function HomeLink({ kind }: { kind: string }) {
  const home = KIND_HOME[kind];
  if (!home) return null;
  return (
    <Link className={styles.fixLink} to={home} search={keepSearch}>
      Open the {kindLabel(kind).toLowerCase()} register ›
    </Link>
  );
}

type Detail = {
  /** The record's own name, for the page header. Null until the owning query answers. */
  title: string | null;
  pairs: Array<[string, string]>;
  /** Why these facts and not others, or where the rest live. */
  because?: string;
  /** What to say when there are no facts to show — never a blank panel (rule 5). */
  absent?: string;
  /** The read's **failure**, where the owning query failed rather than answered. Kept apart from
   * `absent` because the two are opposite instructions: *"loading the register…"* says wait, and a
   * failure says try again — and a reader told the first when the second is true waits for ever,
   * which is what every branch below used to do (EXP-29). */
  failed?: unknown;
  /** Ask the owning register again. */
  retry?: () => void;
  /** For a document: its state, which decides what its page can show of the figures it became. */
  documentState?: string;
  /** Which version of the record the work tab is acting against. **1 unless the kind has a real
   * version column**: a number invented here would be a safety claim nothing checks. Today only a
   * document has one, which is why `expected_version` exists on its decisions and nowhere else. */
  revision: number;
};

/** The facts for one kind, read from whoever owns them.
 *
 * Every branch calls its hook unconditionally — a hook called inside an `if` is the one React rule
 * that fails at runtime rather than at compile time — so each query carries its own `enabled` and
 * only the kind on screen is fetched.
 */
function useKindDetail(kind: string, recordId: string, screen: Screen): Detail {
  const risksRead = useRisks(true, kind === "climate_risk");
  const planRead = usePlan(kind === "initiative");
  const coverageRead = useCoverage(
    screen.window?.period_start,
    screen.window?.period_end,
    kind === "expected_source",
  );
  const facilitiesRead = useFacilities(kind === "facility");
  const documentRead = useDocument(recordId, kind === "document");
  const exchangeRead = useExchange(kind === "supplier_request");
  // **A filing obligation's record id is the obligation id** (ADR-0037), so the report the Reports
  // screen builds is one read away - and it is what an Approver needs in front of them to decide
  // (walkthrough C13). Disabled unless this is a filing: `useReport` is `enabled` on the id.
  const reportRead = useReport(kind === "filing_obligation" ? recordId : undefined, screen.method);
  const risks = risksRead.data;
  const plan = planRead.data;
  const coverage = coverageRead.data;
  const facilities = facilitiesRead.data;
  const document = documentRead.data;

  if (kind === "climate_risk") {
    const risk = risks?.find((r) => r.id === recordId);
    if (!risk) {
      return {
        title: null,
        pairs: [],
        absent: "Loading the register…",
        failed: risksRead.error,
        retry: () => void risksRead.refetch(),
        revision: 1,
      };
    }
    return {
      title: risk.title,
      revision: 1,
      pairs: [
        ["Category", risk.category.replace(/_/g, " ")],
        ["Horizon", risk.horizon.replace(/_/g, " ")],
        ["Strategy", risk.strategy.replace(/_/g, " ")],
        // **Words, not a product.** `basis.Score` refuses to be multiplied into a loss (RSK-01),
        // and a score rendered as "4 × 5 = 20" here would undo that refusal in a template.
        ["Score", risk.score],
        ["State", risk.state.replace(/_/g, " ")],
        ["Next review", risk.next_review_on ? shortDate(risk.next_review_on) : "not set"],
        ["Open actions", String(risk.actions.filter((a) => !a.completed).length)],
      ],
      because:
        risk.review_overdue || risk.needs_rescoring
          ? "This risk needs attention on the register: it is overdue for review, or it was scored " +
            "on a scale that has since been superseded. Rescoring happens there, against a named " +
            "scale, because a score with no scale is a number nobody can interpret."
          : undefined,
    };
  }

  if (kind === "initiative") {
    const initiative = plan?.initiatives.find((i) => i.id === recordId);
    if (!initiative) {
      return {
        title: null,
        pairs: [],
        absent: "Loading the plan…",
        failed: planRead.error,
        retry: () => void planRead.refetch(),
        revision: 1,
      };
    }
    const pairs: Array<[string, string]> = [["Name", initiative.name]];
    pairs.push([
      "Expected saving",
      initiative.expected
        ? `${quantity(initiative.expected)} ${initiative.expected_unit ?? ""}`.trim()
        : "none stated",
    ]);
    pairs.push([
      "Achieved",
      initiative.achieved
        ? `${quantity(initiative.achieved)} ${initiative.achieved_unit ?? ""}`.trim()
        : "not yet measured",
    ]);
    pairs.push(["Evidence", String(initiative.evidence_document_ids.length)]);
    return {
      title: initiative.name,
      pairs,
      revision: 1,
      because:
        "An expected saving is a projection and an achieved one is a measurement, and they are " +
        "two facts here because a form that took both at once is a form where somebody types " +
        "their hope into the measured column. Neither is added to a total: the plan says why.",
    };
  }

  if (kind === "expected_source") {
    const source = coverage?.sources.find((s) => s.source_id === recordId);
    if (!source) {
      return {
        title: null,
        pairs: [],
        // **No window is not "nothing expected in the window"** — the census was never asked, and
        // the second sentence sends a reader to a period selector that has nothing to offer.
        absent: !screen.window
          ? "The expected-bills check is read for a reporting period, and this workspace has not set one up."
          : "This source expects nothing in the period on screen. Change the period in the bar " +
            "above, or open the expected-bills check.",
        // **That sentence is a claim about the census**, and a failed read produced it — which
        // would send somebody to change a period that was never the problem.
        failed: coverageRead.error,
        retry: () => void coverageRead.refetch(),
        revision: 1,
      };
    }
    return {
      title: source.label,
      revision: 1,
      pairs: [
        ["Site", source.facility_name],
        ["Meter", source.meter_label ?? "no meter"],
        ["Category", source.category.replace(/_/g, " ")],
        ["Carrier", source.fuel_or_carrier ?? "not stated"],
        ["Cadence", source.cadence.replace(/_/g, " ")],
        ["Submitted", String(source.submitted)],
        ["Missing", String(source.missing)],
        ["Pending", String(source.pending)],
        ["Retired", source.is_retired ? "yes" : "no"],
      ],
      because:
        "Missing and pending are different facts and are counted separately: a window that has " +
        "not closed yet is pending, not missing, and an expected-bills check that called it missing would hold " +
        "a filing on a bill that is not late.",
    };
  }

  if (kind === "document") {
    if (!document) {
      return {
        title: null,
        pairs: [],
        absent: "Loading the document…",
        failed: documentRead.error,
        retry: () => void documentRead.refetch(),
        revision: 1,
      };
    }
    return {
      title: document.original_filename,
      // **A document has a real version**, and this is the one kind where the number means
      // something: `expected_version` on a review decision refuses a decision taken against a
      // version that has since changed.
      revision: document.version,
      pairs: [
        ["Kind", document.kind.replace(/_/g, " ")],
        ["State", document.state.replace(/_/g, " ")],
        ["Period", `${shortDate(document.period_start)} – ${shortDate(document.period_end)}`],
        ["SHA-256", document.sha256],
      ],
      // The page shows the document and its figures (`RecordDocument.tsx`); the sentence about the
      // review transition that stood here is behind the info control beside "Details".
      documentState: document.state,
    };
  }

  if (kind === "supplier_request") {
    // Borrowed from the exchange list (EXP-14) - the Suppliers screen's own read - and never from a
    // second query for one request.
    const row = exchangeRead.data?.find((r) => r.id === recordId);
    if (!row) {
      return {
        title: null,
        pairs: [],
        absent: "Loading the supplier exchange…",
        failed: exchangeRead.error,
        retry: () => void exchangeRead.refetch(),
        revision: 1,
      };
    }
    return {
      title: `${row.fuel_or_carrier.replace(/_/g, " ")} · ${row.category.replace(/_/g, " ")}`,
      revision: 1,
      pairs: [
        ["Asked of", `supplier workspace ${row.supplier_org_id}`],
        ["Period", `${shortDate(row.period_start)} – ${shortDate(row.period_end)}`],
        ["State", row.state.replace(/_/g, " ")],
        ["Due", row.due_on ? shortDate(row.due_on) : "no date set"],
        ["Answers", String(row.submissions.length)],
      ],
      because:
        "A request is answered, accepted and revoked on the Suppliers screen, and this supplier's " +
        "whole account - submissions, activity, agreements, responsiveness and history - is on " +
        "its account page. This page shows the work attached to the request, not a second copy " +
        "of the exchange.",
    };
  }

  if (kind === "filing_obligation") {
    const report = reportRead.data;
    if (!report) {
      return {
        title: null,
        pairs: [],
        absent: reportRead.error ? undefined : "Loading the filing…",
        failed: reportRead.error,
        retry: () => void reportRead.refetch(),
        revision: 1,
      };
    }
    const blocking = report.findings.filter((f) => f.severity === "blocking").length;
    const pairs: Array<[string, string]> = [
      ["Legal entity", report.context.legal_entity_name],
      ["Regime", report.regime_name],
      ["Period", `${shortDate(report.period_start)} – ${shortDate(report.period_end)}`],
      ["Scope 2 method", report.scope2_method],
      ["Stated total", `${quantity(report.stated_total_tonnes, 1)} tCO₂e`],
      ["Checks", VERDICT_HEADLINE[report.verdict] ?? report.verdict],
      ["Blocking findings", String(blocking)],
    ];
    // **The figures an Approver is signing.** Every emissions line the regulation requires, with its
    // value - the same lines the stated total sums, from the same read the Reports screen renders.
    for (const line of report.lines) {
      if (line.measure !== "emissions" || line.basis !== "regulation") continue;
      pairs.push([
        line.label,
        line.value === null ? "not produced" : `${quantity(line.value, 1)} ${line.unit ?? ""}`.trim(),
      ]);
    }
    return {
      title: `${report.context.legal_entity_name} · ${report.regime_name}`,
      revision: 1,
      pairs,
      because: report.verdict_reason,
    };
  }

  if (kind === "facility") {
    const site = facilities?.find((f) => f.id === recordId);
    if (!site) {
      return {
        title: null,
        pairs: [],
        absent: "Loading the sites…",
        failed: facilitiesRead.error,
        retry: () => void facilitiesRead.refetch(),
        revision: 1,
      };
    }
    return {
      title: site.name,
      revision: 1,
      pairs: [
        ["Legal entity", site.legal_entity_name],
        ["Jurisdiction", site.jurisdiction],
        ["Type", site.facility_type.replace(/_/g, " ")],
        ["Lease treatment", site.lease_type.replace(/_/g, " ")],
        [
          "Operated by the company",
          site.operates === null ? "nobody has said" : site.operates ? "yes" : "no",
        ],
      ],
      because:
        site.lease_type === "unknown"
          ? "An unstated lease treatment holds every filing this site contributes to, and the " +
            "figures are still calculated and still keep their receipts. The question is answered " +
            "on the organisation screen — the GHG Protocol says to ask the company's accountant."
          : undefined,
    };
  }

  return {
    title: null,
    pairs: [],
    revision: 1,
    absent: whereTheDetailLives(kind),
  };
}

/** For the three kinds with no single read of their own.
 *
 * Fetching the whole organisation to fill a header would be a query a reader did not ask for, and
 * one that answers a question this page is not for. Saying where the facts live is both shorter and
 * true.
 */
function whereTheDetailLives(kind: string): string {
  if (kind === "meter") {
    return (
      "A meter's reference, unit and operational dates are on its site, under Organisation — a " +
      "meter is not listed on its own because a reading without its site is a reading nobody can " +
      "place. The work attached to this meter is on the Workflow tab."
    );
  }
  if (kind === "filing_obligation") {
    return (
      "A filing obligation is reached through Reports rather than listed: it is the pairing of a " +
      "regime with a legal entity and a period, and Readiness is where every one of them is shown " +
      "with the rule that produced it. The work attached to this filing is on the Workflow tab."
    );
  }
  if (kind === "disclosure_statement") {
    return (
      "A disclosure statement is what the company itself says, and it is edited inside the report " +
      "it belongs to, where the paragraph requiring it is printed beside it. Its approval is " +
      "invalidated by an edit, because a signature on a paragraph is a signature on the words. " +
      "The work attached to it is on the Workflow tab."
    );
  }
  return (
    "This build does not know what kind of record this is, which means EmitIQ is newer than " +
    "this page. The work attached to it still reads correctly on the Workflow tab."
  );
}
